import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import * as Sentry from '@sentry/react'
import { GraphQLError } from 'graphql/error/GraphQLError'
import { ErrorConfig } from 'graphql/types'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

export const useApolloClient = (
    graphQLServerURI: string,
    setError: (error: ErrorConfig) => void,
): ApolloClient<unknown> | undefined => {
    const [apolloClient, setApolloClient] = React.useState<ApolloClient<unknown>>()
    const { t } = useTranslation()

    const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
        // Second-factor mail can be requested before a recipient exists; never block the UI for it.
        if (operation.operationName === 'SendSecondFactor') {
            return
        }
        if (graphQLErrors) {
            let auxErr: string[] = []
            auxErr = graphQLErrors.map((graphQLError: GraphQLError): string => {
                return graphQLError.extensions && graphQLError.extensions.code
                    ? t('error.' + graphQLError.extensions.code)
                    : graphQLError.message
            })
            setError({
                title: t('graphQLErrorTitle'),
                content: t('graphQLErrorContent') + ':',
                messages: auxErr,
            })
            Sentry.captureException(graphQLErrors)
        } else if (networkError) {
            setError({
                title: t('networkErrorTitle'),
                content: t('networkErrorContent'),
            })
            Sentry.captureException(networkError)
        }
    })

    const httpLink = new HttpLink({ uri: graphQLServerURI })

    const link = ApolloLink.from([errorLink, httpLink])

    React.useEffect(
        () => {
            setApolloClient(
                new ApolloClient({
                    cache: new InMemoryCache(),
                    link: link,
                }),
            )
        },
        // eslint-disable-next-line
        [],
    )

    return apolloClient
}
